<script setup>
/* global axios */
import { ref, computed, watch, onBeforeUnmount } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';

const props = defineProps({
  contactId: { type: [String, Number], required: true },
});
const route = useRoute();
const router = useRouter();
const members = ref([]);
const isCurrentContact = member =>
  String(member.id) === String(props.contactId);
// The other contacts of the same customer; the current one is not shown
// (layout of 2026-10-14).
const linkedMembers = computed(() =>
  members.value.filter(member => !isCurrentContact(member))
);
const loading = ref(false);
const error = ref('');
const canManage = ref(false);
const busy = ref(false);
const dialog = ref(null);
const mode = ref('search');
const query = ref('');
const candidates = ref([]);
const selected = ref(null);
const confirmed = ref(false);
const searching = ref(false);
const candidatePage = ref(1);
const candidateHasMore = ref(false);
const searched = ref(false);
const dialogActive = ref(false);
const searchError = ref('');
const dialogError = ref('');
let searchTimer;
let composing = false;
const histories = ref({});
const menuContactId = ref(null);
const openingId = ref(null);
const removeMenuItems = [
  { label: '解除关联', action: 'remove', icon: 'i-lucide-unlink' },
];
const emptyHistory = {
  items: [],
  loading: false,
  error: '',
  hasMore: false,
  loaded: false,
};
const historyFor = member => histories.value[String(member.id)] || emptyHistory;
let generation = 0;
let searchGeneration = 0;
const base = computed(
  () =>
    `/api/v1/accounts/${route.params.accountId}/contacts/${props.contactId}/customer_links`
);
const canConfirm = computed(
  () =>
    dialogActive.value &&
    confirmed.value &&
    !!selected.value &&
    !searching.value
);
const statusLabel = status =>
  ({ open: '打开', resolved: '已解决', pending: '待处理', snoozed: '已暂停' })[
    status
  ] || status;
const statusTone = status =>
  ({
    open: 'bg-n-blue-3 text-n-blue-11',
    resolved: 'bg-n-teal-3 text-n-teal-11',
    pending: 'bg-n-amber-3 text-n-amber-11',
  })[status] || 'bg-n-alpha-2 text-n-slate-11';
// The conversation's last activity as 2026/10/12 (seconds from the server).
const activityDate = conversation => {
  if (!conversation.last_activity_at) return '';
  const at = new Date(conversation.last_activity_at * 1000);
  return `${at.getFullYear()}/${at.getMonth() + 1}/${at.getDate()}`;
};
const displayError = e =>
  e?.response?.data?.error || '操作失败，请刷新后重试。';

async function load() {
  closeDialog();
  const version = ++generation;
  searchGeneration++;
  searching.value = false;
  loading.value = true;
  error.value = '';
  members.value = [];
  canManage.value = false;
  histories.value = {};
  menuContactId.value = null;
  openingId.value = null;
  dialog.value?.close();
  try {
    const { data } = await axios.get(base.value);
    if (version !== generation) return;
    members.value = data.members;
    canManage.value = data.can_manage;
    // Records are shown directly on every linked card, not behind a toggle.
    members.value
      .filter(member => !isCurrentContact(member))
      .forEach(member => loadHistory(member, true));
  } catch (e) {
    if (version === generation) error.value = displayError(e);
  } finally {
    if (version === generation) loading.value = false;
  }
}

function openAdd() {
  closeDialog();
  mode.value = 'search';
  query.value = '';
  candidates.value = [];
  selected.value = null;
  confirmed.value = false;
  dialogError.value = '';
  searchError.value = '';
  dialogActive.value = true;
  dialog.value.open();
  search();
}

function closeDialog() {
  clearTimeout(searchTimer);
  searchGeneration++;
  dialogActive.value = false;
  composing = false;
  searching.value = false;
  selected.value = null;
  confirmed.value = false;
}

function scheduleSearch() {
  clearTimeout(searchTimer);
  if (!dialogActive.value || mode.value !== 'search' || busy.value || composing)
    return;
  const value = query.value.trim();
  if (!value) search();
  else if (value.length >= 2 && value.length <= 100)
    searchTimer = setTimeout(() => search(), 400);
}

function searchNow() {
  clearTimeout(searchTimer);
  if (!composing) search();
}

async function search(append = false) {
  clearTimeout(searchTimer);
  if (!dialogActive.value || mode.value !== 'search' || busy.value) return;
  const value = query.value.trim();
  if ((value && value.length < 2) || value.length > 100) return;
  if (append && (searching.value || !candidateHasMore.value)) return;
  const version = ++searchGeneration;
  const current = generation;
  const page = append ? candidatePage.value + 1 : 1;
  searching.value = true;
  searchError.value = '';
  if (!append) {
    selected.value = null;
    confirmed.value = false;
    candidates.value = [];
    candidateHasMore.value = false;
    searched.value = false;
  }
  try {
    const { data } = await axios.get(`${base.value}/candidates`, {
      params: { q: value, page },
    });
    if (
      current !== generation ||
      version !== searchGeneration ||
      value !== query.value.trim()
    )
      return;
    const ids = new Set(candidates.value.map(candidate => candidate.id));
    candidates.value.push(
      ...data.candidates.filter(candidate => !ids.has(candidate.id))
    );
    candidatePage.value = page;
    candidateHasMore.value = data.has_more;
    searched.value = true;
  } catch (e) {
    if (current === generation && version === searchGeneration)
      searchError.value = displayError(e);
  } finally {
    if (version === searchGeneration) searching.value = false;
  }
}

function select(candidate) {
  if (busy.value || searching.value || !dialogActive.value) return;
  selected.value = candidate;
  confirmed.value = false;
  dialogError.value = '';
}

function remove(member) {
  closeDialog();
  menuContactId.value = null;
  searchGeneration++;
  searching.value = false;
  mode.value = 'remove';
  selected.value = member;
  confirmed.value = false;
  dialogError.value = '';
  dialogActive.value = true;
  dialog.value.open();
}

async function submit() {
  if (!canConfirm.value || busy.value) return;
  const current = generation;
  const url = base.value;
  const operation = mode.value;
  const targetId = selected.value.id;
  busy.value = true;
  dialogError.value = '';
  try {
    if (operation === 'remove') await axios.delete(`${url}/${targetId}`);
    else
      await axios.post(url, {
        target_contact_id: targetId,
        confirmed: true,
      });
    if (current !== generation) return;
    dialog.value.close();
    useAlert(
      operation === 'remove'
        ? '已解除关联，原联系人和会话保留。'
        : '已关联，可继续添加其他客户资料。'
    );
    await load();
  } catch (e) {
    if (current === generation && dialogActive.value)
      dialogError.value = displayError(e);
  } finally {
    busy.value = false;
  }
}

async function loadHistory(member, reset = false) {
  const key = String(member.id);
  if (!histories.value[key])
    histories.value[key] = { ...emptyHistory, items: [], page: 1 };
  const state = histories.value[key];
  if (state.loading) return;
  const current = generation;
  state.loading = true;
  state.error = '';
  if (reset) {
    state.items = [];
    state.page = 1;
    state.hasMore = false;
    state.loaded = false;
  }
  try {
    const { data } = await axios.get(`${base.value}/history`, {
      params: { page: state.page, target_contact_id: member.id },
    });
    if (current !== generation) return;
    const ids = new Set(state.items.map(c => c.id));
    state.items.push(
      ...data.conversations.filter(
        c => String(c.contact_id) === key && !ids.has(c.id)
      )
    );
    state.hasMore = data.has_more;
    state.page++;
    state.loaded = true;
  } catch (e) {
    if (current === generation) state.error = displayError(e);
  } finally {
    if (current === generation) state.loading = false;
  }
}

async function openConversation(conversation) {
  if (openingId.value !== null) return;
  const current = generation;
  openingId.value = conversation.id;
  try {
    const { data } = await axios.post(`${base.value}/open`, {
      conversation_id: conversation.id,
    });
    if (current === generation)
      await router.push(
        `/app/accounts/${route.params.accountId}/conversations/${data.conversation_id}`
      );
  } catch (e) {
    if (current === generation) useAlert(displayError(e));
  } finally {
    if (current === generation) openingId.value = null;
  }
}

watch(() => [props.contactId, route.params.accountId], load, {
  immediate: true,
  flush: 'sync',
});
watch(
  query,
  () => {
    searchGeneration++;
    candidates.value = [];
    selected.value = null;
    confirmed.value = false;
    searching.value = false;
    candidateHasMore.value = false;
    searched.value = false;
    searchError.value = '';
    dialogError.value = '';
    scheduleSearch();
  },
  { flush: 'sync' }
);
onBeforeUnmount(() => {
  closeDialog();
  generation++;
  searchGeneration++;
});
</script>

<template>
  <section
    class="w-full rounded-xl border border-n-weak bg-n-solid-2 p-3"
    aria-label="客户关联"
  >
    <div class="flex items-center gap-2">
      <h4 class="m-0 text-sm font-medium">客户关联</h4>
      <span
        v-if="linkedMembers.length"
        class="inline-flex items-center justify-center h-5 min-w-5 px-1.5 rounded-full bg-n-alpha-2 text-xs text-n-slate-11"
        data-test="customer-links-count"
        >{{ linkedMembers.length }}</span
      >
      <span class="flex-1" />
      <Button
        v-if="canManage"
        label="添加关联"
        icon="i-lucide-link"
        size="xs"
        variant="ghost"
        :disabled="busy || loading"
        @click="openAdd"
      />
    </div>
    <p v-if="loading" role="status" class="m-0 mt-3 text-sm text-n-slate-11">
      正在加载联系方式…
    </p>
    <div v-else-if="linkedMembers.length" class="flex flex-col">
      <article
        v-for="member in linkedMembers"
        :key="member.id"
        class="min-w-0 pt-3 mt-3 border-t border-n-weak"
        :aria-label="member.name || '未命名联系人'"
      >
        <div class="flex items-start gap-2.5">
          <Avatar
            :name="member.name || '未命名联系人'"
            :size="32"
            rounded-full
            class="shrink-0"
          />
          <div class="flex-1 min-w-0">
            <div class="flex flex-wrap items-center gap-x-1.5 gap-y-1 min-w-0">
              <span
                class="text-sm font-semibold break-words min-w-0"
                :title="member.name"
                >{{ member.name || '未命名联系人' }}</span
              >
              <span
                v-if="member.preferred"
                class="px-1.5 py-0.5 text-xs rounded-md bg-n-blue-3 text-n-blue-11 shrink-0"
                >常用</span
              >
            </div>
            <p
              v-if="member.email"
              class="flex items-center gap-1.5 m-0 mt-0.5 text-[13px] leading-5 text-n-slate-12"
            >
              <Icon
                icon="i-lucide-mail"
                class="shrink-0 size-3.5 text-n-slate-11"
              />
              <span class="min-w-0 break-all">{{ member.email }}</span>
            </p>
            <p
              v-if="member.phone_number"
              class="flex items-center gap-1.5 m-0 mt-0.5 text-[13px] leading-5 text-n-slate-12"
            >
              <Icon
                icon="i-lucide-phone"
                class="shrink-0 size-3.5 text-n-slate-11"
              />
              <span class="min-w-0 break-all">{{ member.phone_number }}</span>
            </p>
            <p
              class="m-0 mt-0.5 text-xs leading-[18px] text-n-slate-11 break-words"
            >
              {{
                member.inboxes.map(i => i.name).join('、') || '暂无已绑定收件箱'
              }}
            </p>
          </div>
          <div
            v-if="canManage"
            v-on-clickaway="() => (menuContactId = null)"
            class="relative shrink-0 -mt-1 -me-1.5"
            @keydown.esc="menuContactId = null"
          >
            <Button
              icon="i-lucide-ellipsis"
              size="xs"
              variant="ghost"
              color="slate"
              :aria-label="`管理${member.name || '联系人'}关联`"
              :aria-expanded="menuContactId === member.id"
              :disabled="busy"
              @click="
                menuContactId = menuContactId === member.id ? null : member.id
              "
            />
            <DropdownMenu
              v-if="menuContactId === member.id"
              :menu-items="removeMenuItems"
              class="ltr:right-0 rtl:left-0 mt-1 w-40 top-full"
              @action="remove(member)"
            />
          </div>
        </div>
        <div
          :id="`customer-history-${member.id}`"
          class="mt-2 ms-[42px]"
          aria-label="联系记录"
          :aria-busy="historyFor(member).loading"
        >
          <div
            class="flex flex-col -mx-2 max-h-60 overflow-x-hidden overflow-y-auto"
          >
            <button
              v-for="conversation in historyFor(member).items"
              :key="conversation.id"
              type="button"
              class="flex items-center gap-2 w-full h-9 px-2 text-left rounded-md hover:bg-n-alpha-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand disabled:opacity-50"
              :title="conversation.inbox_name"
              :disabled="openingId !== null"
              @click="openConversation(conversation)"
            >
              <span class="text-[13px] font-medium tabular-nums"
                >#{{ conversation.id }}</span
              >
              <span
                class="shrink-0 px-1.5 rounded text-xs leading-5"
                :class="statusTone(conversation.status)"
                >{{ statusLabel(conversation.status) }}</span
              >
              <span class="flex-1 min-w-0 truncate text-xs text-n-slate-11">{{
                activityDate(conversation)
              }}</span>
              <Icon
                icon="i-lucide-chevron-right"
                class="size-3.5 shrink-0 text-n-slate-11"
              />
            </button>
          </div>
          <p
            v-if="historyFor(member).error"
            role="alert"
            class="m-0 py-2 text-xs text-n-ruby-11"
          >
            {{ historyFor(member).error }}
          </p>
          <Button
            v-if="historyFor(member).error && !historyFor(member).loading"
            label="重试"
            size="xs"
            variant="ghost"
            @click="loadHistory(member)"
          />
          <p
            v-if="historyFor(member).loading"
            role="status"
            class="m-0 py-2 text-xs text-n-slate-11"
          >
            正在加载记录…
          </p>
          <p
            v-else-if="
              historyFor(member).loaded &&
              !historyFor(member).items.length &&
              !historyFor(member).error
            "
            class="m-0 py-2 text-xs text-n-slate-11"
          >
            暂无可查看的历史会话。
          </p>
          <Button
            v-if="historyFor(member).hasMore && !historyFor(member).error"
            label="加载更多"
            size="xs"
            color="slate"
            variant="ghost"
            :disabled="historyFor(member).loading"
            @click="loadHistory(member)"
          />
        </div>
      </article>
    </div>
    <p
      v-else-if="!error"
      class="m-0 mt-3 px-3 py-3.5 text-center text-[13px] leading-5 text-n-slate-11 border border-dashed border-n-weak rounded-lg"
      data-test="customer-links-empty"
    >
      还没有关联其他联系方式<br />关联后，可在这里打开同一客户在其他渠道的会话
    </p>
    <p v-if="error" role="alert" class="mt-2 text-sm text-n-ruby-11">
      {{ error }}
    </p>
    <Button
      v-if="error && !loading"
      label="重新加载"
      size="sm"
      variant="ghost"
      @click="load"
    />
    <Dialog
      ref="dialog"
      :title="mode === 'remove' ? '解除客户关联' : '添加客户关联'"
      :description="
        mode === 'remove' ? '解除后，原联系人、消息和会话仍保留。' : ''
      "
      :confirm-button-label="mode === 'remove' ? '解除关联' : '确认关联'"
      cancel-button-label="取消"
      :disable-confirm-button="!canConfirm"
      :is-loading="busy"
      overflow-y-auto
      @confirm="submit"
      @close="closeDialog"
    >
      <template v-if="mode === 'search'" #description>
        <Button
          icon="i-lucide-x"
          aria-label="关闭添加客户关联"
          size="xs"
          color="slate"
          variant="ghost"
          class="absolute top-4 right-4"
          @click="dialog.close()"
        />
        <p class="m-0 text-sm text-n-slate-11 break-words">
          找到同一客户在其他渠道的联系人，关联后可在会话面板查看他的会话。
        </p>
      </template>
      <div v-if="mode === 'search'" class="flex flex-col gap-3">
        <div>
          <div class="relative">
            <Input
              v-model="query"
              label="搜索客户"
              placeholder="姓名、邮箱或带国家区号的号码"
              custom-input-class="!pl-9 !pr-10"
              :maxlength="100"
              :disabled="busy"
              autofocus
              @keydown.enter.prevent
              @enter="searchNow"
              @compositionstart="
                composing = true;
                clearTimeout(searchTimer);
              "
              @compositionend="
                composing = false;
                scheduleSearch();
              "
            >
              <template #prefix>
                <Icon
                  icon="i-lucide-search"
                  class="absolute bottom-3 left-3 size-4 text-n-slate-11 pointer-events-none"
                />
              </template>
            </Input>
            <Button
              v-if="query"
              icon="i-lucide-x"
              aria-label="清空搜索"
              size="xs"
              color="slate"
              variant="ghost"
              class="absolute bottom-1.5 right-1.5"
              :disabled="busy"
              @click="query = ''"
            />
          </div>
          <p class="m-0 mt-1.5 text-xs text-n-slate-11">
            至少输入 2 个字符；号码请带国家区号，如 +39
          </p>
        </div>
        <div
          v-if="candidates.length"
          class="flex items-center justify-between text-xs text-n-slate-11"
          data-test="customer-links-found"
        >
          <span>{{
            candidateHasMore
              ? `已显示 ${candidates.length} 位客户`
              : `找到 ${candidates.length} 位客户`
          }}</span>
          <span>选择一位</span>
        </div>
        <div
          class="flex flex-col gap-1 h-72 max-h-[40vh] overflow-y-auto"
          aria-label="客户列表"
          :aria-busy="searching"
        >
          <label
            v-for="candidate in candidates"
            :key="candidate.id"
            class="flex items-start gap-3 p-3 m-0 text-start border rounded-lg shrink-0 cursor-pointer focus-within:outline focus-within:outline-2 focus-within:-outline-offset-2 focus-within:outline-n-brand"
            :class="
              selected?.id === candidate.id
                ? 'bg-n-blue-2 border-n-blue-7'
                : 'border-transparent hover:bg-n-alpha-2'
            "
          >
            <Avatar
              :name="candidate.name || '未命名联系人'"
              :size="36"
              aria-hidden="true"
              class="shrink-0"
            />
            <span class="flex-1 min-w-0">
              <span
                class="block text-sm font-semibold break-words"
                :title="candidate.name"
                >{{ candidate.name || '未命名联系人' }}</span
              >
              <span
                v-if="candidate.email"
                class="flex items-center gap-1.5 mt-0.5 text-[13px] leading-5 text-n-slate-12 break-all"
              >
                <Icon
                  icon="i-lucide-mail"
                  class="shrink-0 size-3.5 text-n-slate-11"
                />
                {{ candidate.email }}
              </span>
              <span
                v-if="candidate.phone_number"
                class="flex items-center gap-1.5 mt-0.5 text-[13px] leading-5 text-n-slate-12 break-all"
              >
                <Icon
                  icon="i-lucide-phone"
                  class="shrink-0 size-3.5 text-n-slate-11"
                />
                {{ candidate.phone_number }}
              </span>
              <span
                v-if="candidate.inboxes?.length"
                class="inline-block mt-1.5 px-1.5 rounded bg-n-alpha-2 text-xs leading-5 text-n-slate-11"
                >{{ candidate.inboxes.map(i => i.name).join('、') }}</span
              >
            </span>
            <input
              type="radio"
              :name="`customer-link-${route.params.accountId}-${contactId}`"
              :value="candidate.id"
              :checked="selected?.id === candidate.id"
              :disabled="busy || searching"
              class="m-0 mt-2.5 shrink-0 size-4 accent-n-brand"
              @change="select(candidate)"
            />
          </label>
          <p
            v-if="searching"
            role="status"
            class="m-0 p-3 text-sm text-n-slate-11"
          >
            正在加载客户列表…
          </p>
          <div v-else-if="searchError" class="p-3">
            <p role="alert" class="m-0 mb-2 text-sm text-n-ruby-11">
              {{ searchError }}
            </p>
            <Button
              label="重试搜索"
              size="sm"
              variant="ghost"
              @click="search(candidates.length > 0)"
            />
          </div>
          <p
            v-else-if="searched && !candidates.length"
            role="status"
            class="m-0 p-3 text-sm text-n-slate-11"
          >
            {{
              query.trim()
                ? '未找到匹配的客户，请更换姓名、邮箱或号码搜索。'
                : '暂无可关联的客户。'
            }}
          </p>
          <p
            v-else-if="!searched"
            role="status"
            class="m-0 p-3 text-xs text-n-slate-11"
          >
            输入至少两个字符即可搜索。
          </p>
          <Button
            v-if="candidateHasMore && !searchError"
            label="加载更多"
            size="sm"
            variant="ghost"
            class="shrink-0 mx-auto"
            :disabled="searching || busy"
            @click="search(true)"
          />
        </div>
        <div class="flex flex-col gap-3 pt-4 border-t border-n-weak">
          <p
            class="m-0 text-sm text-n-slate-12 break-words"
            aria-live="polite"
            data-test="customer-links-selected"
          >
            <template v-if="selected">
              <span class="text-n-slate-11">将关联：</span>
              <strong class="font-semibold">{{
                selected.name || '未命名联系人'
              }}</strong>
              <template v-if="selected.email || selected.phone_number">
                · {{ selected.email || selected.phone_number }}
              </template>
            </template>
            <template v-else>请先选择要关联的客户</template>
          </p>
          <label
            class="flex items-start gap-2 m-0 text-sm text-n-slate-12 cursor-pointer"
          >
            <input
              v-model="confirmed"
              type="checkbox"
              class="mt-1 shrink-0 accent-n-brand"
              :disabled="!selected || busy || searching"
            />
            <span
              >我已核对，确认是同一客户<span class="text-n-slate-11"
                >（可随时解除，原联系人和会话不变）</span
              ></span
            >
          </label>
        </div>
      </div>
      <p v-else class="break-words text-n-slate-12">
        解除与
        {{ selected?.name || selected?.email || selected?.phone_number }}
        的关联？
      </p>
      <label v-if="mode === 'remove'" class="flex gap-2 items-start text-sm">
        <input
          v-model="confirmed"
          type="checkbox"
          class="mt-1"
          :disabled="busy"
        />
        <span>确认只解除关联，保留原资料。</span>
      </label>
      <p v-if="dialogError" role="alert" class="m-0 text-sm text-n-ruby-11">
        {{ dialogError }}
      </p>
    </Dialog>
  </section>
</template>
